import { useState, useEffect, useRef } from 'react';
import { Modal } from '~/components/ui/Modal';
import { setAllSupabaseData, updateSupabaseConnectionStatus } from '~/lib/stores/integrations';
import { projectId } from '~/lib/persistence';
import { toast } from 'react-toastify';
import { useTranslation } from '~/lib/hooks/useTranslation';

interface Organization {
  id: string;
  name: string;
}

interface Project {
  id: string;
  name: string;
  status: string;
  created_at?: string;
}

interface ProjectCredentials {
  anon_key: string;
}

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
}

interface SupabaseApiError {
  error: string;
}

interface SupabaseOauthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (credentials: {
    projectId: string;
    projectName: string;
    organizationId: string;
    organizationName: string;
    anonKey: string;
  }) => void;
  onError: (error: { message: string }) => void;
  currentProjectId?: string;
  currentProjectName?: string;
  currentOrganizationId?: string;
  currentOrganizationName?: string;
  setShowSupabaseOAuthModal?: (isOpen: boolean) => void;
}

export function SupabaseOauthModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  currentProjectId,
  currentProjectName,
  currentOrganizationId,
  currentOrganizationName,
  setShowSupabaseOAuthModal,
}: SupabaseOauthModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'auth' | 'organizations' | 'projects' | 'credentials'>('auth');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [accessToken, setAccessToken] = useState<string>('');
  console.log('isOpen >>>', isOpen);

  // Add timeout management
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }

      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Function to stop loading and clear timeout
  const stopLoading = () => {
    setLoading(false);
  };

  // Check for existing token when modal opens
  useEffect(() => {
    const checkToken = async () => {
      console.log('refresh token useEffect is called each time the modal is mounted 2');

      const storedToken = localStorage.getItem('supabase_access_token');

      if (storedToken) {
        // Validate token before using it
        const result = await validateAndUseToken(storedToken);
        console.log('result >>>', result);
      } else {
        setStep('auth');
        handleLogout();
      }
    };
    checkToken();
  }, [isOpen]);

  // Function to refresh the token
  const refreshToken = async (token: string): Promise<string> => {
    try {
      // console.log('refreshing token', token, 'step', step);

      const response = await fetch('/api/supabase/refresh-token', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = (await response.json()) as TokenResponse;
      const newToken = data.access_token;
      const newRefreshToken = data.refresh_token;

      // console.log('newToken refreshed', newToken, newRefreshToken, data);

      if (newToken && newRefreshToken) {
        setAccessToken(newToken);
        localStorage.setItem('supabase_access_token', newToken);
        localStorage.setItem('supabase_refresh_token', newRefreshToken);
      } else if (newToken && !newRefreshToken) {
        setAccessToken(newToken);
        localStorage.setItem('supabase_access_token', newToken);
        localStorage.removeItem('supabase_refresh_token');
      }

      return newToken;
    } catch (error) {
      console.error('[Supabase Auth] Token refresh failed:', error);
      throw error;
    }
  };
  console.log('refresh token useEffect is called each time the modal is mounted');

  // Set up automatic token refresh
  useEffect(() => {
    // Refresh token every 45 minutes to ensure it never expires
    const refreshInterval = setInterval(
      async () => {
        try {
          const token = localStorage.getItem('supabase_refresh_token');

          if (!token) {
            return;
          }

          const newToken = await refreshToken(token);
          setAccessToken(newToken);
        } catch (error) {
          console.error('[Supabase Auth] Auto-refresh failed:', error);

          // If refresh fails, we'll need to re-authenticate
          handleLogout();
        }
      },
      15 * 60 * 1000, // 15 minutes
    );

    return () => clearInterval(refreshInterval);
  }, []);

  const validateAndUseToken = async (token: string) => {
    try {
      console.log('[Supabase Auth] Starting token validation...');
      setLoading(true);

      // Try to fetch organizations to validate token
      const response = await fetch('/api/supabase/organizations?path=organizations', {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
      });

      // console.log('[Supabase Auth] Validation response status:', response.status);

      if (!response.ok) {
        if (response.status === 401) {
          console.error('[Supabase Auth] Token validation failed with 401');
          localStorage.removeItem('supabase_access_token');
          localStorage.removeItem('supabase_refresh_token');
          setAccessToken('');
          setStep('auth');
          throw new Error('Authentication expired. Please login again.');
        }

        const errorData = (await response.json()) as SupabaseApiError;
        console.error('[Supabase Auth] Token validation failed:', errorData);
        throw new Error(errorData.error || 'Failed to validate token');
      }

      const orgs = (await response.json()) as Organization[];
      console.log('[Supabase Auth] Organizations fetched:', orgs);

      if (!orgs || orgs.length === 0) {
        throw new Error('No organizations found');
      }

      // Token is valid, set it and update state
      console.log('[Supabase Auth] Token validated successfully');
      setAccessToken(token);
      setOrganizations(orgs);

      // Try to find the current organization or use the first one
      const currentOrg = currentOrganizationId ? orgs.find((org) => org.id === currentOrganizationId) : orgs[0];
      console.log('currentOrg data >>>', currentOrg);

      if (currentOrg) {
        setSelectedOrganization(currentOrg);
        setStep('projects');
        await fetchProjects(currentOrg.id);
      } else {
        // No valid organization found; request parent to show OAuth modal
        if (setShowSupabaseOAuthModal) {
          setShowSupabaseOAuthModal(true);
        }

        throw new Error('No valid organization found');
      }
    } catch (error) {
      console.error('[Supabase Auth] Token validation error:', error);
      localStorage.removeItem('supabase_access_token');
      localStorage.removeItem('supabase_refresh_token');
      setAccessToken('');
      setStep('auth');
      stopLoading();
      throw error; // Propagate error for proper handling
    }
  };

  const handleMessage = async (event: MessageEvent) => {
    console.log('[Supabase Auth] Received message:', event.data);

    if (event.origin !== window.location.origin) {
      console.log('[Supabase Auth] Message from different origin:', event.origin);
      return;
    }

    if (event.data?.type === 'SUPABASE_AUTH_SUCCESS') {
      try {
        console.log('[Supabase Auth] Processing auth success message', event.data);

        const { token, refresh_token: refreshTokenFromMessage } = event.data as {
          token?: string;
          refresh_token?: string | null;
        };

        if (!token) {
          console.error('[Supabase Auth] No token in success message');
          throw new Error('No token received');
        }

        console.log('[Supabase Auth] Storing token and validating...');
        localStorage.setItem('supabase_access_token', token);

        if (refreshTokenFromMessage) {
          localStorage.setItem('supabase_refresh_token', refreshTokenFromMessage);
        }

        // Then validate and use it
        await validateAndUseToken(token);

        // Only remove listener after successful validation
        window.removeEventListener('message', handleMessage);
        console.log('[Supabase Auth] Auth flow completed successfully');
      } catch (error) {
        console.error('[Supabase Auth] Error in auth success flow:', error);
        localStorage.removeItem('supabase_access_token');
        localStorage.removeItem('supabase_refresh_token');
        setAccessToken('');
        setStep('auth');
        stopLoading();
        onError({ message: error instanceof Error ? error.message : 'Failed to authenticate' });
      }
    } else if (event.data?.type === 'SUPABASE_AUTH_ERROR') {
      console.error('[Supabase Auth] Received auth error:', event.data.error);
      window.removeEventListener('message', handleMessage);
      stopLoading();
      onError({ message: event.data.error || 'Authentication failed' });
    }
  };

  // Update handleAuth to properly clean up on error
  const handleAuth = async () => {
    try {
      console.log('[Supabase Auth] Starting auth flow...');
      localStorage.removeItem('supabase_access_token');
      localStorage.removeItem('supabase_refresh_token');
      setAccessToken('');
      setStep('auth');
      setLoading(true);

      // Add message listener before opening popup
      window.addEventListener('message', handleMessage);
      console.log('[Supabase Auth] Added message listener');

      // Open popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      console.log('[Supabase Auth] Opening auth popup...');

      const popup = window.open(
        '/auth/supabase',
        'Supabase Login',
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      if (!popup) {
        throw new Error('Failed to open popup window. Please allow popups for this site.');
      }

      // Focus the popup
      popup.focus();

      let isAuthenticated = false;
      let hasHandledClosure = false;
      let pollAttempts = 0;
      const maxPollAttempts = 35;

      // Poll localStorage every second to check for token
      pollIntervalRef.current = setInterval(() => {
        pollAttempts++;

        const token = localStorage.getItem('supabase_access_token');
        console.log('[Supabase Auth] Polling attempt', pollAttempts, 'token exists:', !!token);

        if (token) {
          console.log('[Supabase Auth] Token found in localStorage, stopping polling');
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          isAuthenticated = true;
          hasHandledClosure = true;
          stopLoading();
          validateAndUseToken(token);
        }

        if (pollAttempts >= maxPollAttempts && !isAuthenticated) {
          console.log('[Supabase Auth] Max poll attempts reached');
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          hasHandledClosure = true;
          stopLoading();
          window.removeEventListener('message', handleMessage);
          onError({ message: 'Authentication timeout. Please try again.' });
        }
      }, 1000);

      // Set a timeout to cleanup
      setTimeout(
        () => {
          if (!isAuthenticated && !hasHandledClosure && pollIntervalRef.current) {
            console.log('[Supabase Auth] Final timeout cleanup');
            hasHandledClosure = true;
            clearInterval(pollIntervalRef.current!);
            pollIntervalRef.current = null;
            stopLoading();
            window.removeEventListener('message', handleMessage);
            onError({ message: 'Authentication timeout. Please try again.' });
          }
        },
        maxPollAttempts * 1000 + 1000,
      );
    } catch (error) {
      console.error('[Supabase Auth] Error in auth flow:', error);
      stopLoading();
      window.removeEventListener('message', handleMessage);
      onError({ message: error instanceof Error ? error.message : 'An unknown error occurred' });
    }
  };

  const fetchProjects = async (orgId: string) => {
    try {
      setLoading(true);
      console.log('[Supabase Auth] Fetching projects for org:', orgId);

      const supabaseToken = localStorage.getItem('supabase_access_token');

      if (!supabaseToken) {
        handleLogout();
        throw new Error('No access token found');
      } else if (!orgId || orgId === '') {
        throw new Error('No organization ID found');
      }

      // Use the same token format as in validateAndUseToken
      const response = await fetch(`/api/supabase/organizations/?path=organizations&organization_id=${orgId}`, {
        headers: {
          Authorization: supabaseToken,
          'Content-Type': 'application/json',
        },
      });

      // console.log('[Supabase Auth] Projects response:', response);

      if (!response.ok) {
        if (response.status === 401) {
          console.error('[Supabase Auth] Project fetch failed with 401');
          localStorage.removeItem('supabase_access_token');
          localStorage.removeItem('supabase_refresh_token');
          setStep('auth');
          throw new Error('Authentication expired. Please login again.');
        }

        const errorData = (await response.json()) as SupabaseApiError;
        console.error('[Supabase Auth] Project fetch failed:', errorData);
        throw new Error(errorData.error || 'Failed to fetch projects');
      }

      const projectsData = (await response.json()) as Project[];
      console.log('[Supabase Auth] Projects data:', projectsData);
      setProjects(projectsData);
      stopLoading();
    } catch (error) {
      console.error('[Supabase Auth] Error fetching projects:', error);
      stopLoading();
      onError({ message: error instanceof Error ? error.message : 'Failed to fetch projects' });
    }
  };

  const handleCreateProject = async () => {
    window.open(`https://supabase.com/dashboard/new/${selectedOrganization?.id}`, '_blank');
  };

  const handleProjectSelection = async (project: Project) => {
    setSelectedProject(project);
  };

  const handleProjectSelect = async (project: Project) => {
    try {
      setLoading(true);

      console.log('Fetching credentials for project:', project.id);

      const response = await fetch(`/api/supabase/organizations?path=projects/${project.id}/api-keys`, {
        headers: {
          Authorization: accessToken,
          'Content-Type': 'application/json',
        },
      });

      // console.log('Credentials response:', response);

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('supabase_access_token');
          localStorage.removeItem('supabase_refresh_token');
          setStep('auth');
          throw new Error('Authentication expired. Please login again.');
        }

        const errorData = (await response.json()) as SupabaseApiError;
        console.log('errorData', errorData);

        throw new Error(errorData.error || 'Failed to fetch project credentials');
      }

      const credentials = (await response.json()) as ProjectCredentials;

      // console.log('Credentials received successfully', credentials);

      setAllSupabaseData(
        project.id,
        project.name,
        credentials.anon_key,
        selectedOrganization?.id || '',
        selectedOrganization?.name || '',
      );
      updateSupabaseConnectionStatus(true, false, accessToken);

      // Configure Supabase project with redirect URLs after successful connection
      try {
        console.log('[Supabase Modal] Configuring project redirect URLs...');

        const configResponse = await fetch('/api/supabase/config', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            projectId: project.id,
          }),
        });

        if (configResponse.ok) {
          const configResult = await configResponse.json();

          // console.log('[Supabase Modal] Project configured successfully:', configResult);
        } else {
          const configError = await configResponse.json();
          console.warn('[Supabase Modal] Failed to auto-configure redirect URLs:', configError);

          // Don't fail the entire flow, just log the warning
        }
      } catch (configError) {
        console.warn('[Supabase Modal] Error configuring redirect URLs:', configError);

        // Don't fail the entire flow, just log the warning
      }

      onSuccess({
        projectId: project.id,
        projectName: project.name,
        organizationId: selectedOrganization?.id || '',
        organizationName: selectedOrganization?.name || '',
        anonKey: credentials.anon_key,
      });

      onClose();
    } catch (error) {
      console.error('Error fetching project credentials:', error);
      stopLoading(); // Error - stop loading
      onError({ message: error instanceof Error ? error.message : 'Failed to fetch project credentials' });
    }
  };

  const handleMakeProjectActive = async (project: Project) => {
    // console.log('handleMakeProjectActive: ', project);
    window.open(`https://supabase.com/dashboard/project/${project.id}`, '_blank');
  };

  const handleLogout = () => {
    localStorage.removeItem('supabase_access_token');
    localStorage.removeItem('supabase_refresh_token');
    setAccessToken('');
    setOrganizations([]);
    setSelectedOrganization(null);
    setProjects([]);
    setSelectedProject(null);
    setStep('auth');
  };

  const handleClose = () => {
    // Clear the polling interval
    console.log('[Supabase Auth] Closing modal');

    if (!loading) {
      onClose();
      return;
    }

    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    console.log('[Supabase Auth] Cleared polling interval');

    toast.error('Authentication failed. Please try again.');
    console.log('[Supabase Auth] Set error');

    setLoading(false);
    console.log('[Supabase Auth] Set loading to false');

    setStep('auth');
    console.log('[Supabase Auth] Set step to auth');

    // Clear any message listeners
    window.removeEventListener('message', handleMessage);
    console.log('[Supabase Auth] Cleared message listeners');

    // Call the original onClose
    onClose();
    console.log('[Supabase Auth] Called onClose');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return '12/05/25';
    }

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
    } catch {
      return '12/05/25';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-50`} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-51 w-full px-4 max-w-[580px] max-h-[600px] overflow-y-auto">
        <div className="bg-alpha-background-secondary rounded-xl border border-white/10 relative w-full overflow-hidden">
          {/* Header */}
          <div className="flex flex-col p-4 border-b border-white/10">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <svg width="20" height="20" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="96" height="96" rx="12" fill="white" fillOpacity="0.2" />
                    <g clipPath="url(#clip0_118_228)">
                      <path
                        d="M53.2589 78.936C51.6254 80.9945 48.3069 79.867 48.2704 77.2375L47.6924 38.7845H73.5499C78.2324 38.7845 80.8434 44.193 77.9314 47.859L53.2589 78.936Z"
                        fill="url(#paint0_linear_118_228)"
                      />
                      <path
                        d="M53.2589 78.936C51.6254 80.9945 48.3069 79.867 48.2704 77.2375L47.6924 38.7845H73.5499C78.2324 38.7845 80.8434 44.193 77.9314 47.859L53.2589 78.936Z"
                        fill="url(#paint1_linear_118_228)"
                        fillOpacity="0.2"
                      />
                      <path
                        d="M42.7421 17.064C44.3756 15.0055 47.6946 16.133 47.7306 18.762L47.9846 57.2155H22.4511C17.7686 57.2155 15.1576 51.807 18.0691 48.141L42.7421 17.064Z"
                        fill="#3ECF8E"
                      />
                    </g>
                    <defs>
                      <linearGradient
                        id="paint0_linear_118_228"
                        x1="47.6947"
                        y1="47.3131"
                        x2="70.6748"
                        y2="56.9508"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#249361" />
                        <stop offset="1" stopColor="#3ECF8E" />
                      </linearGradient>
                      <linearGradient
                        id="paint1_linear_118_228"
                        x1="37.5064"
                        y1="33.3635"
                        x2="47.9863"
                        y2="53.0923"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop />
                        <stop offset="1" stopOpacity="0" />
                      </linearGradient>
                      <clipPath id="clip0_118_228">
                        <rect width="64" height="64" fill="white" transform="translate(16 16)" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span className="text-[#e7e7e7] font-semibold">
                    {accessToken ? t('modals.supabase.connectedTitle') : t('modals.supabase.title')}
                  </span>
                  <button
                    className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors px-2 py-1 mt-1 rounded-full bg-white/10 hover:bg-white/20 cursor-pointer"
                    onClick={() => window.open('https://alphadocs.dualite.dev/integrations/supabase', '_blank')}
                  >
                    <span className="text-xs text-[#e7e7e7]/70">{t('modals.supabase.docs')}</span>
                    <div className="i-ph:link w-3 h-3" />
                  </button>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-1 text-[#e7e7e7] bg-transparent border-none rounded-md hover:bg-white/10 transition-colors"
              >
                <div className="i-ph:x w-4 h-4" />
              </button>
            </div>
            {!!projectId.get() && currentProjectId && step !== 'auth' && (
              <div className="mt-2 flex items-center gap-2 text-xs text-[#e7e7e7]/70 bg-[#1A1A1A] p-2 rounded-md">
                <div className="i-ph:info w-4 h-4" />
                <span>{t('modals.supabase.viewOnlyMode')}</span>
              </div>
            )}
            {currentProjectId && currentProjectName && currentOrganizationName && step === 'auth' && (
              <div className="mt-2 flex flex-col gap-2 text-xs text-[#e7e7e7]/70 bg-blue-500/10 border border-blue-500/20 p-3 rounded-md">
                <div className="flex items-center gap-2">
                  <div className="i-ph:check-circle w-4 h-4 text-blue-400" />
                  <span className="font-medium">{t('modals.supabase.reconnectMessage')}</span>
                </div>
                <div className="ml-6">
                  <p className="mb-1">{t('modals.supabase.reconnectMessage')}</p>

                  <p className="flex items-center gap-2">
                    <strong>{t('modals.supabase.organization')}</strong>
                    <span className="bg-white/20 px-2 py-1 rounded text-[#e7e7e7] font-medium">
                      {currentOrganizationName}
                    </span>
                  </p>
                  <p className="flex items-center gap-2">
                    <strong>{t('modals.supabase.project')}</strong>
                    <span className="bg-white/20 px-2 py-1 rounded text-[#e7e7e7] font-medium">
                      {currentProjectName}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {step === 'auth' && (
              <>
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="i-svg-spinners:90-ring-with-bg w-8 h-8 text-white mb-4" />
                    <p className="text-sm text-[#e7e7e7]/70">{t('modals.supabase.checkingConnection')}</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="text-center">
                      <svg
                        width="96"
                        height="96"
                        viewBox="0 0 96 96"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="mx-auto mb-6"
                      >
                        <rect width="96" height="96" rx="12" fill="white" fillOpacity="0.2" />
                        <g clipPath="url(#clip0_118_228)">
                          <path
                            d="M53.2589 78.936C51.6254 80.9945 48.3069 79.867 48.2704 77.2375L47.6924 38.7845H73.5499C78.2324 38.7845 80.8434 44.193 77.9314 47.859L53.2589 78.936Z"
                            fill="url(#paint0_linear_118_228)"
                          />
                          <path
                            d="M53.2589 78.936C51.6254 80.9945 48.3069 79.867 48.2704 77.2375L47.6924 38.7845H73.5499C78.2324 38.7845 80.8434 44.193 77.9314 47.859L53.2589 78.936Z"
                            fill="url(#paint1_linear_118_228)"
                            fillOpacity="0.2"
                          />
                          <path
                            d="M42.7421 17.064C44.3756 15.0055 47.6946 16.133 47.7306 18.762L47.9846 57.2155H22.4511C17.7686 57.2155 15.1576 51.807 18.0691 48.141L42.7421 17.064Z"
                            fill="#3ECF8E"
                          />
                        </g>
                        <defs>
                          <linearGradient
                            id="paint0_linear_118_228"
                            x1="47.6947"
                            y1="47.3131"
                            x2="70.6748"
                            y2="56.9508"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#249361" />
                            <stop offset="1" stopColor="#3ECF8E" />
                          </linearGradient>
                          <linearGradient
                            id="paint1_linear_118_228"
                            x1="37.5064"
                            y1="33.3635"
                            x2="47.9863"
                            y2="53.0923"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop />
                            <stop offset="1" stopOpacity="0" />
                          </linearGradient>
                          <clipPath id="clip0_118_228">
                            <rect width="64" height="64" fill="white" transform="translate(16 16)" />
                          </clipPath>
                        </defs>
                      </svg>
                      <h3 className="text-[#e7e7e7] text-sm font-semibold mb-1">
                        {currentProjectId && currentProjectName && currentOrganizationName
                          ? t('modals.supabase.tocontinue')
                          : t('modals.supabase.linkAccount')}
                      </h3>
                      <p className="text-[#e7e7e7]/70 text-xs">{t('modals.supabase.openSecureWindow')}</p>
                    </div>

                    <div className="flex justify-center">
                      <button
                        onClick={handleAuth}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-white/90 text-black rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        <span>{t('modals.supabase.signInWithSupabase')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {step === 'projects' && (
              <div className="space-y-6">
                {/* Header with Organization Name and Action Buttons */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <h2 className="text-[#e7e7e7] text-[20px] font-semibold">
                        {selectedOrganization?.name || organizations[0]?.name || 'Your Organization'}
                      </h2>
                      <div className="flex items-center gap-2 mb-1">
                        <button
                          className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors  px-2 py-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={loading || (!!projectId.get() && !!currentProjectId)}
                          onClick={handleLogout}
                        >
                          <span className="text-xs text-[#e7e7e7]/70 pt-2">{t('modals.supabase.switch')}</span>
                          <div className="i-ph:arrows-left-right w-3 h-3 mt-2" />
                        </button>
                      </div>
                    </div>
                    <p className="text-[#e7e7e7]/50 text-sm mt-1">{t('modals.supabase.organizationLabel')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        const orgId = selectedOrganization?.id || organizations[0]?.id;

                        if (orgId) {
                          fetchProjects(orgId);
                        }
                      }}
                      disabled={loading || (!!projectId.get() && !!currentProjectId)}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-[#1A1A1A] border border-white/10 text-[#e7e7e7] rounded-lg hover:bg-[#252525] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="i-ph:arrows-clockwise w-4 h-4" />
                      <span>{t('modals.supabase.sync')}</span>
                    </button>
                    {!projectId.get() && (
                      <button
                        onClick={handleCreateProject}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-white text-black rounded-lg hover:bg-white/90 transition-colors"
                      >
                        <div className="i-ph:plus w-4 h-4" />
                        <span>{t('modals.supabase.createProject')}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Projects Content */}
                {loading ? (
                  <div className="flex flex-col items-center justify-center w-full h-[150px] pb-20">
                    <div className="i-svg-spinners:90-ring-with-bg w-6 h-6 mb-2 bg-white/10 rounded-full p-2" />
                    <span className="text-[#e7e7e7]/70 text-sm">{t('modals.supabase.loadingProjects')}</span>
                  </div>
                ) : projects.length > 0 ? (
                  <div>
                    <div className="grid grid-cols-2 gap-4 pb-4">
                      {projects.map((project) => (
                        <button
                          key={project.id}
                          onClick={() => handleProjectSelection(project)}
                          disabled={!!projectId.get() && !!currentProjectId && project.id !== currentProjectId}
                          className={`p-4 border rounded-lg transition-colors ${!!projectId.get() && !!currentProjectId && project.id !== currentProjectId ? 'opacity-50 cursor-not-allowed' : ''} text-left flex flex-col h-[70px] justify-between relative ${
                            selectedProject?.id === project.id || project.id === currentProjectId
                              ? 'bg-[#1A1A1A] border-white/20'
                              : 'bg-[#141414] border-white/10 hover:bg-[#1A1A1A]'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <div className="text-[#e7e7e7] font-medium text-sm">{project.name}</div>
                            </div>
                            <div className="flex items-center gap-[6px]">
                              {project.status !== 'ACTIVE_HEALTHY' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMakeProjectActive(project);
                                  }}
                                  className="text-xs bg-[#1A1A1A] rounded-md px-2 py-1 text-[#e7e7e7]/50 hover:text-[#e7e7e7] hover:bg-[#252525] transition-colors flex items-center gap-1"
                                >
                                  <div className="i-ph:warning w-3.5 h-3.5 text-red-500" />
                                  <span className="underline text-red-500">{t('modals.supabase.activate')}</span>
                                </button>
                              )}
                              {(selectedProject?.id === project.id || project.id === currentProjectId) &&
                                project.status === 'ACTIVE_HEALTHY' && (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <circle cx="8" cy="8" r="7" fill="white" />
                                    <path
                                      d="M11.5 5.5L6.75 10.25L4.5 8"
                                      stroke="black"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                            </div>
                          </div>
                          <div className="text-[#e7e7e7]/50 text-xs">{formatDate(project.created_at)}</div>
                        </button>
                      ))}
                    </div>
                    <button
                      disabled={
                        !selectedProject ||
                        selectedProject.status !== 'ACTIVE_HEALTHY' ||
                        (!!projectId.get() && !!currentProjectId)
                      }
                      onClick={() => {
                        if (selectedProject) {
                          handleProjectSelect(selectedProject);
                          setStep('credentials');
                        }
                      }}
                      className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl transition-all text-base font-medium ${
                        selectedProject &&
                        selectedProject.status === 'ACTIVE_HEALTHY' &&
                        !(projectId.get() && currentProjectId)
                          ? 'bg-white text-black hover:bg-white/90 shadow-lg shadow-white/10'
                          : 'bg-[#1A1A1A] border border-white/10 text-[#e7e7e7]/50 hover:bg-[#252525]'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      title={
                        projectId.get() && currentProjectId
                          ? t('modals.supabase.projectAlreadyConnected')
                          : t('modals.supabase.selectProject')
                      }
                    >
                      <span>{t('modals.supabase.selectProject')}</span>
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center mx-auto mb-6">
                      <svg width="96" height="97" viewBox="0 0 96 97" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect y="0.5" width="96" height="96" rx="12" fill="white" fillOpacity="0.2" />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M21.5182 29.8557C21.3333 30.8359 21.3333 32.0126 21.3333 34.366V53.1667C21.3333 63.2232 21.3333 68.2518 24.4574 71.3758C27.5817 74.5 32.6099 74.5 42.6666 74.5H53.3333C63.3898 74.5 68.4183 74.5 71.5423 71.3758C74.6666 68.2518 74.6666 63.2232 74.6666 53.1667V47.2944C74.6666 40.2748 74.6666 36.765 72.6146 34.4836C72.426 34.2737 72.2263 34.074 72.0165 33.8852C69.7349 31.8334 66.2253 31.8334 59.2055 31.8334H58.209C55.1325 31.8334 53.5943 31.8334 52.161 31.4248C51.3735 31.2003 50.6143 30.8859 49.8988 30.4878C48.5965 29.7631 47.5087 28.6754 45.3333 26.5L43.8658 25.0327C43.1367 24.3036 42.7722 23.939 42.3891 23.6214C40.7373 22.2521 38.711 21.4128 36.5748 21.213C36.0793 21.1667 35.5637 21.1667 34.5326 21.1667C32.1792 21.1667 31.0025 21.1667 30.0223 21.3516C25.7074 22.1657 22.3323 25.5408 21.5182 29.8557ZM47.9999 45.8334C49.1045 45.8334 49.9999 46.7288 49.9999 47.8334V51.1667H53.3333C54.4378 51.1667 55.3332 52.0622 55.3332 53.1667C55.3332 54.2712 54.4378 55.1667 53.3333 55.1667H49.9999V58.5C49.9999 59.6046 49.1045 60.5 47.9999 60.5C46.8954 60.5 45.9999 59.6046 45.9999 58.5V55.1667H42.6666C41.562 55.1667 40.6666 54.2712 40.6666 53.1667C40.6666 52.0622 41.562 51.1667 42.6666 51.1667H45.9999V47.8334C45.9999 46.7288 46.8954 45.8334 47.9999 45.8334Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    <h3 className="text-[#e7e7e7] font-bold text-xl mb-2">{t('modals.supabase.noProjectsTitle')}</h3>
                    <p className="text-[#e7e7e7]/70 text-sm mb-8">{t('modals.supabase.noProjectsMessage')}</p>
                    <button
                      onClick={handleCreateProject}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-white/90 text-black rounded-lg transition-all text-sm font-medium"
                    >
                      <span>{t('modals.supabase.createSupabaseProject')}</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {step === 'credentials' && (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="i-svg-spinners:90-ring-with-bg w-8 h-8 text-white" />
                <p className="text-sm text-[#e7e7e7]/70">{t('modals.supabase.retrievingCredentials')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
